import { memo, useState, type FC } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Fade from "@mui/material/Fade";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { MatchDonutChart } from "./MatchDonutChart";

function formatDateTime(timestamp: string | null): string {
  if (!timestamp) return "-";
  return new Date(timestamp).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
}

type SuccessStateProps = {
  fileName: string | null;
  totalRows: number | null;
  matchedRows: number | null;
  invalidRows: number | null;
  uploadedAt: string | null;
  /** Omit to hide the "Last Matched On" row (no async matching step). */
  matchedAt?: string | null;
  showSuccessBanner: boolean;
  fileId: string | null;
  onClear: () => void;
  /** Omit to hide the "View Match Details" button. */
  onDownloadDetails?: (fileId: string) => Promise<void>;
  /** Prefix for the buttons' data-testids; defaults to "uploaded-accounts". */
  testIdPrefix?: string;
};

export const SuccessState: FC<SuccessStateProps> = memo(
  ({
    fileName,
    totalRows,
    matchedRows,
    invalidRows,
    uploadedAt,
    matchedAt,
    showSuccessBanner,
    fileId,
    onClear,
    onDownloadDetails,
    testIdPrefix = "uploaded-accounts",
  }) => {
    const [isDownloading, setIsDownloading] = useState(false);

    const total = totalRows || 0;
    const matched = matchedRows || 0;
    const invalid = invalidRows || 0;
    const unmatched = Math.max(0, total - matched - invalid);

    const handleDownloadDetails = async () => {
      if (!fileId || !onDownloadDetails) return;
      setIsDownloading(true);
      try {
        await onDownloadDetails(fileId);
      } finally {
        setIsDownloading(false);
      }
    };

    return (
      <Box>
        <Fade in={showSuccessBanner} timeout={300} unmountOnExit>
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{ borderRadius: 0 }}
          >
            <AlertTitle sx={{ mb: 0 }}>Upload complete!</AlertTitle>
            {total.toLocaleString()} accounts processed
          </Alert>
        </Fade>

        <Box sx={{ px: 2, py: 2 }}>
          <Box sx={{ mb: 3 }}>
            <MatchDonutChart
              matched={matched}
              unmatched={unmatched}
              invalid={invalid}
              total={total}
            />
          </Box>

          <Stack spacing={1.5} sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CheckCircleOutlineIcon color="success" fontSize="small" />
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                Matches
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {matched.toLocaleString()}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1.5}>
              <HelpOutlineIcon color="warning" fontSize="small" />
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                No match found
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {unmatched.toLocaleString()}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1.5}>
              <ErrorOutlineIcon color="error" fontSize="small" />
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                Invalid Inputs
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {invalid.toLocaleString()}
              </Typography>
            </Stack>
          </Stack>

          <Box
            sx={{
              bgcolor: "grey.50",
              p: 2,
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Stack spacing={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Typography variant="caption" color="text.secondary">
                  File Name:
                </Typography>
                <Typography
                  variant="caption"
                  color="text.primary"
                  align="right"
                  sx={{
                    wordBreak: "break-all",
                    maxWidth: "60%",
                  }}
                >
                  {fileName}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  Uploaded On:
                </Typography>
                <Typography variant="caption" color="text.primary">
                  {formatDateTime(uploadedAt)}
                </Typography>
              </Stack>
              {matchedAt !== undefined && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    Last Matched On:
                  </Typography>
                  <Typography variant="caption" color="text.primary">
                    {formatDateTime(matchedAt)}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>

          <Stack spacing={1}>
            {onDownloadDetails && (
              <Button
                variant="contained"
                size="small"
                fullWidth
                onClick={handleDownloadDetails}
                data-testid={`${testIdPrefix}-view-details-btn`}
                disabled={isDownloading || !fileId}
                startIcon={
                  isDownloading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
              >
                {isDownloading ? "Downloading..." : "View Match Details"}
              </Button>
            )}

            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={onClear}
              data-testid={`${testIdPrefix}-clear-btn`}
            >
              Upload Different File
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }
);

SuccessState.displayName = "SuccessState";
